import type { Channel } from '../features/chat/chat.types';
import { apiFetch } from '../utils/apiClient';

const CHANNEL_COLORS = [
  '#8B5CF6', '#6366F1', '#EC4899', '#F59E0B',
  '#10B981', '#3B82F6', '#EF4444', '#14B8A6',
];


function parseNamespacedAmpId(namespaced: string): { ampId: number; type: string } {
  const colonIdx = namespaced.indexOf(':');
  if (colonIdx === -1) {
    // Legacy plain numeric ID — treat as custom
    return { ampId: Number(namespaced), type: 'custom' };
  }
  return {
    type: namespaced.slice(0, colonIdx),
    ampId: Number(namespaced.slice(colonIdx + 1)),
  };
}

function mapApiChannelToChannel(apiChannel: any): Channel {
  const id = String(apiChannel.id);
  const colorIndex = apiChannel.id % CHANNEL_COLORS.length;
  return {
    id,
    guid: id,
    name: apiChannel.name || 'Unnamed Channel',
    description: apiChannel.description || '',
    icon: apiChannel.icon || 'hash',
    color: CHANNEL_COLORS[colorIndex],
    ampIds: (apiChannel.amps || [])
      .filter((a: any) => a.ampId != null && a.type != null)
      .map((a: any) => `${a.type}:${a.ampId}`),
    mcpIds: (apiChannel.mcpIds || [])
      .filter((m: any) => m.mcpId != null && m.name != null)
      .map((m: any) => ({ name: m.name, mcpId: m.mcpId })),
    skillIds: (apiChannel.skillIds || [])
      .filter((s: any) => s.skillId != null && s.name != null)
      .map((s: any) => ({ name: s.name, skillId: s.skillId })),
    shareWithCompany: typeof apiChannel.shareWithCompany === 'boolean' ? apiChannel.shareWithCompany : undefined,
    createdAt: new Date().toISOString(),
  };
}

export async function getChannels(): Promise<Channel[]> {
  const data = await apiFetch<any[]>(`${process.env.REACT_APP_AccountAMP_Url}/api/ChannelManagement/list`, {
    method: 'GET',
  });
  const list = Array.isArray(data) ? data : [];
  return list.map(mapApiChannelToChannel);
}

interface UpsertChannelBody {
  id: number;
  name: string;
  description: string;
  icon: string;
  shareWithCompany: boolean;
  amps: { ampId: number; type: string }[];
  mcpIds?: { name: string; mcpId: string }[];
  skillIds?: { name: string; skillId: string }[];
}

async function upsertChannel(body: UpsertChannelBody): Promise<Channel> {
  const data = await apiFetch<any>(`${process.env.REACT_APP_AccountAMP_Url}/api/ChannelManagement/upsert`, {
    method: 'POST',
    body: JSON.stringify(body),
    skipCache: true, // Don't cache POST requests
  });
  return mapApiChannelToChannel(data);
}

export async function createChannel(data: Omit<Channel, 'id' | 'guid' | 'createdAt'>): Promise<Channel> {
  return upsertChannel({
    id: 0,
    name: data.name,
    description: data.description || '',
    icon: data.icon || 'hash',
    shareWithCompany: true,
    amps: data.ampIds.map(parseNamespacedAmpId),
    mcpIds: data.mcpIds ?? [],
    skillIds: data.skillIds ?? [],
  });
}

export async function updateChannel(channelId: string, updates: Partial<Channel>): Promise<Channel> {
  return upsertChannel({
    id: Number(channelId),
    name: updates.name || '',
    description: updates.description || '',
    icon: updates.icon || 'hash',
    // Keep a private workspace private when the caller passes its current setting.
    shareWithCompany: updates.shareWithCompany ?? true,
    amps: (updates.ampIds || []).map(parseNamespacedAmpId),
    mcpIds: updates.mcpIds ?? [],
    skillIds: updates.skillIds ?? [],
  });
}

export async function addAmpToChannel(channel: Channel, ampId: string): Promise<Channel> {
  if (channel.ampIds.includes(ampId)) return channel;

  const { ampId: parsedAmpId, type } = parseNamespacedAmpId(ampId);

  await apiFetch(`${process.env.REACT_APP_AccountAMP_Url}/api/ChannelManagement/addamp`, {
    method: 'POST',
    body: JSON.stringify({
      channelId: Number(channel.id),
      ampId: parsedAmpId,
      type,
    }),
    skipCache: true,
  });

  // Return updated channel with new AMP added locally
  // (actual refresh will happen in the context)
  return {
    ...channel,
    ampIds: [...channel.ampIds, ampId],
  };
}

export async function removeAmpFromChannel(channel: Channel, ampId: string): Promise<Channel> {
  const amps = channel.ampIds
    .filter(id => id !== ampId)
    .map(parseNamespacedAmpId);
  return upsertChannel({
    id: Number(channel.id),
    name: channel.name,
    description: channel.description || '',
    icon: channel.icon || 'hash',
    shareWithCompany: true,
    amps,
    // Upsert replaces the whole channel, so leaving these out wiped the workspace's skills and MCPs.
    mcpIds: channel.mcpIds ?? [],
    skillIds: channel.skillIds ?? [],
  });
}

export async function dismissWelcomeChannel(): Promise<void> {
  await apiFetch(`${process.env.REACT_APP_AccountAMP_Url}/api/channelmanagement/dismisswelcomechannel`, {
    method: 'POST',
    skipCache: true,
  });
}

export async function deleteChannel(channelId: string): Promise<void> {
  await apiFetch(`${process.env.REACT_APP_AccountAMP_Url}/api/ChannelManagement/delete/${channelId}`, {
    method: 'DELETE',
    skipCache: true,
  });
}

export const validateChannel = (data: {
  name: string;
  ampIds: string[];
}): { valid: boolean; error?: string } => {
  if (!data.name.trim()) {
    return { valid: false, error: 'Channel name is required' };
  }
  if (data.name.trim().length > 50) {
    return { valid: false, error: 'Channel name must be 50 characters or less' };
  }
  if (!data.ampIds || data.ampIds.length === 0) {
    return { valid: false, error: 'At least one AMP must be selected' };
  }
  return { valid: true };
};
